import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, Clock, ArrowRight, Music, Heart, Users } from "lucide-react";
import axios from "axios";

import { API, resolveImg } from "@/config";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_bwaise-voices/artifacts/1a5a97ee_IMG_9775.jpeg";
const DEFAULT_HERO = "https://img.youtube.com/vi/hGvRlVi1UsE/maxresdefault.jpg";
const DEFAULT_ABOUT = "https://img.youtube.com/vi/s4g6iamPn_M/maxresdefault.jpg";

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [heroImg, setHeroImg] = useState(DEFAULT_HERO);
  const [aboutImg, setAboutImg] = useState(DEFAULT_ABOUT);
  const [heroText, setHeroText] = useState({ title: "Good Samaritans' Choir Bwaise", motto: "Love And Unity In God", subtitle: "Serving through music since 1996. Lifting hearts and glorifying God through the power of song." });
  const [aboutPreview, setAboutPreview] = useState("For nearly three decades, the Good Samaritans' Choir Bwaise has been a beacon of hope and worship in the Bwaise community. Through the gift of music, we bring people together, uplift spirits, and spread the message of God's love.");
  const [contactInfo, setContactInfo] = useState({ email: "", phone: "", location: "Bwaise, Kampala" });
  const [socialLinks, setSocialLinks] = useState({ youtube: "", facebook: "" });
  const [eventPoster, setEventPoster] = useState({ active: false, image_url: "", alt_text: "", link: "/events", bg_color: "#2D1B69" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, galleryRes, settingsRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/settings`),
        ]);
        setEvents(eventsRes.data.slice(0, 3));
        setGallery(galleryRes.data.slice(0, 4));
        const s = settingsRes.data;
        if (s.hero_image) setHeroImg(resolveImg(s.hero_image));
        if (s.about_image) setAboutImg(resolveImg(s.about_image));
        if (s.hero_text) setHeroText(s.hero_text);
        if (s.about_text?.history_p1) setAboutPreview(s.about_text.history_p1);
        if (s.contact_info) setContactInfo(s.contact_info);
        if (s.social_links) setSocialLinks(s.social_links);
        if (s.event_poster) setEventPoster(prev => ({ ...prev, ...s.event_poster }));
      } catch (err) {
        console.error("Failed to load homepage data:", err.message);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on mount; API is a module constant

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[80vh] sm:min-h-[85vh] flex items-center justify-center"
        style={{ backgroundImage: `url(${heroImg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="hero-section"
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 text-center px-5 sm:px-4 max-w-4xl mx-auto fade-in">
          <img src={LOGO_URL} alt="Choir Logo" className="h-20 w-20 sm:h-24 sm:w-24 md:h-32 md:w-32 rounded-full mx-auto mb-4 sm:mb-6 border-4 border-white/30 shadow-2xl" />
          <h1
            className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white text-shadow tracking-tight mb-3 sm:mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {heroText.title}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-[#FAD6A5] italic mb-2 text-shadow" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            "{heroText.motto}"
          </p>
          <p className="text-white/80 text-sm sm:text-base md:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {heroText.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Link to="/donate" data-testid="hero-donate-btn">
              <Button className="btn-primary text-base px-8 sm:px-10 py-3 sm:py-4 w-full sm:w-auto">Support Our Mission</Button>
            </Link>
            <Link to="/about" data-testid="hero-about-btn">
              <Button className="bg-white/20 text-white hover:bg-white/30 rounded-full px-8 sm:px-10 py-3 sm:py-4 font-semibold backdrop-blur-sm border border-white/30 transition-all hover:-translate-y-0.5 w-full sm:w-auto">
                Our Story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Event Poster Banner */}
      {eventPoster.active && (eventPoster.image_url || eventPoster.video_url) && (
        <section style={{ backgroundColor: eventPoster.bg_color }} className="py-6 sm:py-8" data-testid="event-poster-section">
          <div className="section-container">
            <div className="rounded-2xl overflow-hidden shadow-2xl max-w-4xl mx-auto">
              {eventPoster.media_type === "video" && eventPoster.video_url ? (
                eventPoster.video_url.includes("youtube.com") || eventPoster.video_url.includes("youtu.be") ? (
                  <div className="relative aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${(() => { const m = eventPoster.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/); return m ? m[1] : ""; })()}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={eventPoster.alt_text || "Event video"}
                    />
                  </div>
                ) : (
                  <video src={eventPoster.video_url} className="w-full h-auto rounded-2xl" controls preload="metadata" />
                )
              ) : (
                <a href={eventPoster.link || "/events"} data-testid="event-poster-link">
                  <img
                    src={eventPoster.image_url}
                    alt={eventPoster.alt_text || "Upcoming event"}
                    className="w-full h-auto"
                  />
                </a>
              )}
            </div>
            <p className="text-center text-white/60 text-xs mt-3">
              {eventPoster.media_type === "video" ? "Watch the event promo above" : "Tap the poster for event details"}
            </p>
          </div>
        </section>
      )}

      {/* Stats */}
      <section className="bg-[#1A1A1A] py-8">
        <div className="section-container">
          <div className="grid grid-cols-3 gap-6 text-center">
            {[
              { icon: Music, value: "Since 1996", label: "Years of Service" },
              { icon: Users, value: "Community", label: "Bwaise, Kampala" },
              { icon: Heart, value: "Love & Unity", label: "In God" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <stat.icon size={20} className="text-[#E8922F] mb-1" />
                <p className="text-white font-bold text-sm md:text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stat.value}</p>
                <p className="text-gray-400 text-xs md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="about-preview-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <p className="text-[#C4392D] text-sm uppercase tracking-widest font-semibold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>About Us</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                A Legacy of Worship & Community
              </h2>
              <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed mb-6">
                {aboutPreview}
              </p>
              <Link to="/about" data-testid="about-learn-more">
                <Button className="btn-outline-dark group">
                  Learn More <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={aboutImg} alt="Choir worship" className="w-full h-[350px] md:h-[450px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Preview */}
      {events.length > 0 && (
        <section className="section-padding bg-white" data-testid="events-preview-section">
          <div className="section-container">
            <div className="text-center mb-12">
              <p className="text-[#C4392D] text-sm uppercase tracking-widest font-semibold mb-3">Upcoming</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Events & Services
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map((event) => (
                <div key={event.id} className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow rounded-2xl p-6" data-testid={`event-card-${event.id}`}>
                  <div className="flex items-center gap-2 text-[#E8922F] mb-3">
                    <CalendarDays size={16} />
                    <span className="text-sm font-medium">{event.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{event.title}</h3>
                  <p className="text-[#4A4A4A] text-sm mb-4 line-clamp-2">{event.description}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-[#4A4A4A]">
                    {event.time && (
                      <span className="flex items-center gap-1"><Clock size={12} />{event.time}</span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1"><MapPin size={12} />{event.location}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/events" data-testid="events-view-all">
                <Button className="btn-outline-dark">View All Events <ArrowRight size={16} className="ml-2" /></Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Preview */}
      {gallery.length > 0 && (
        <section className="section-padding bg-[#FFF3E6]" data-testid="gallery-preview-section">
          <div className="section-container">
            <div className="text-center mb-12">
              <p className="text-[#C4392D] text-sm uppercase tracking-widest font-semibold mb-3">Gallery</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Moments of Praise
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {gallery[0] && (
                <div className="md:col-span-7 rounded-2xl overflow-hidden h-[300px] md:h-[400px]">
                  <img src={resolveImg(gallery[0].image_url)} alt={gallery[0].title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              {gallery[1] && (
                <div className="md:col-span-5 rounded-2xl overflow-hidden h-[300px] md:h-[400px]">
                  <img src={resolveImg(gallery[1].image_url)} alt={gallery[1].title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              {gallery[2] && (
                <div className="md:col-span-5 rounded-2xl overflow-hidden h-[250px]">
                  <img src={resolveImg(gallery[2].image_url)} alt={gallery[2].title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              {gallery[3] && (
                <div className="md:col-span-7 rounded-2xl overflow-hidden h-[250px]">
                  <img src={resolveImg(gallery[3].image_url)} alt={gallery[3].title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              )}
            </div>
            <div className="text-center mt-8">
              <Link to="/gallery" data-testid="gallery-view-all">
                <Button className="btn-outline-dark">View Full Gallery <ArrowRight size={16} className="ml-2" /></Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Donate CTA */}
      <section className="section-padding bg-[#C4392D]" data-testid="donate-cta-section">
        <div className="section-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Support Our Mission
          </h2>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Your generous donation helps us continue our ministry of music, support community outreach, and maintain our choir programs.
          </p>
          <Link to="/donate" data-testid="cta-donate-btn">
            <Button className="bg-white text-[#C4392D] hover:bg-gray-100 rounded-full px-10 py-4 font-semibold text-base transition-all hover:-translate-y-0.5">
              Make a Donation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
